import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ConfirmDialog } from '@/components/common';
import type { MechanicGroup } from '@/features/calendar/calendarMechanicPrefs';

interface MechanicGroupsControlProps {
  groups: MechanicGroup[];
  selectedMechanicIds: string[];
  onSave: (name: string) => void;
  onApply: (id: string) => void;
  onDelete: (id: string) => void;
}

export function MechanicGroupsControl({
  groups,
  selectedMechanicIds,
  onSave,
  onApply,
  onDelete,
}: MechanicGroupsControlProps) {
  const { t } = useTranslation('calendar');
  const { t: tc } = useTranslation('common');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [pendingDelete, setPendingDelete] = useState<MechanicGroup | null>(null);

  const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleOpenSave = useCallback(() => {
    setGroupName('');
    setSaveOpen(true);
    setMenuAnchor(null);
  }, []);

  const handleSave = useCallback(() => {
    onSave(groupName);
    setSaveOpen(false);
    setGroupName('');
  }, [groupName, onSave]);

  const canSave = selectedMechanicIds.length > 0 && groupName.trim().length > 0;

  return (
    <>
      <Tooltip title={t('mechanicGroups.title')}>
        <Badge
          badgeContent={groups.length}
          color="primary"
          overlap="circular"
          sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16, padding: '0 4px' } }}
        >
          <IconButton
            size="small"
            onClick={handleOpenMenu}
            aria-label={t('mechanicGroups.title')}
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
          >
            <GroupIcon fontSize="small" />
          </IconButton>
        </Badge>
      </Tooltip>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem
          onClick={handleOpenSave}
          disabled={selectedMechanicIds.length === 0}
        >
          <ListItemIcon>
            <BookmarkAddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('mechanicGroups.saveCurrent')}</ListItemText>
        </MenuItem>
        {groups.length === 0 ? (
          <MenuItem disabled>
            <ListItemText>
              <Typography variant="body2" color="text.secondary">
                {t('mechanicGroups.empty')}
              </Typography>
            </ListItemText>
          </MenuItem>
        ) : (
          groups.map((group) => (
            <MenuItem
              key={group.id}
              onClick={() => {
                onApply(group.id);
                handleCloseMenu();
              }}
              aria-label={`${t('mechanicGroups.apply')}: ${group.name}`}
            >
              <ListItemText
                primary={group.name}
                secondary={String(group.mechanicIds.length)}
              />
              <Tooltip title={t('mechanicGroups.delete')}>
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={t('mechanicGroups.delete')}
                  onClick={(event) => {
                    event.stopPropagation();
                    setPendingDelete(group);
                    handleCloseMenu();
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </MenuItem>
          ))
        )}
      </Menu>

      <Dialog
        open={saveOpen}
        onClose={() => { setSaveOpen(false); }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('mechanicGroups.saveCurrent')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={t('mechanicGroups.name')}
              placeholder={t('mechanicGroups.namePlaceholder')}
              value={groupName}
              onChange={(event) => { setGroupName(event.target.value); }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && canSave) {
                  event.preventDefault();
                  handleSave();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSaveOpen(false); }}>{tc('cancel')}</Button>
          <Button onClick={handleSave} disabled={!canSave} variant="contained">
            {tc('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('mechanicGroups.delete')}
        message={t('mechanicGroups.deleteConfirm', { name: pendingDelete?.name ?? '' })}
        confirmColor="error"
        onConfirm={() => {
          if (pendingDelete) onDelete(pendingDelete.id);
          setPendingDelete(null);
        }}
        onCancel={() => { setPendingDelete(null); }}
      />
    </>
  );
}
